<script>
  import PageBody from "$lib/components/Page/PageBody.svelte";
  import HeroEventDetails from "$lib/components/HeroEventDetails.svelte";
  import LocationInformation from "$lib/components/EventConfirmation/LocationInformation.svelte";
  import Section from "$lib/components/Section.svelte";
  import SEO from "$lib/components/SEO.svelte";
  import SplitLayout from "$lib/components/SplitLayout.svelte";
  import { formatEventTime, formatEventPrices } from "$lib/utilities/eventFormatters.js";
  import { sanitizeSlug } from "$lib/utilities/url-helpers.js";
  import EventPreviewList from "$lib/contentTypes/EventPreviewList.svelte";
  import Static from "$lib/components/MenuTypes/Static/Static.svelte";
  import { Button } from "@ObamaFoundation/of-design-system";
  import { formatTime } from "$lib/utilities/eventDate.js";
  import { formatDate } from "$lib/utilities/date";
  import { getContext, onMount, setContext } from "svelte";
  import { gtag } from "$lib/utilities/gtm.js";
  import SplitLayoutComponent from "$lib/contentTypes/SplitLayoutComponent.svelte";
  // eslint-disable-next-line  @ObamaFoundation/of-svelte/no-stores
  import { page } from "$app/stores";

  export let data;

  let { event, entry, ipls, epls } = data;
  const trailingBody = entry.fields.trailingBody;
  const mainBody = entry.fields.body;
  setContext("ipls", ipls);
  setContext("epls", epls);
  setContext("hrFullWidth", true); // set the horizonal rule on page to be full width

  const featureFlags = getContext("featureFlags") ?? {};
  const eventNoIndex = featureFlags.eventsNoIndex ?? false;
  const isRepeating = event.recurrence === "repeating";
  const epl = {
    fields: {
      ...event.epl?.fields,
      listItems: event.epl?.fields?.listItems.filter((item) => item.fields.slug !== event.slug)
    }
  };

  let sessionId = null;

  const getEventTime = (event, sessionId) => {
    if (isRepeating && event.epl?.fields?.listItems[0]) {
      const matchedSession =
        (sessionId &&
          event.epl.fields.listItems.find((item) => item.fields.sessionId === sessionId)) ||
        event.epl.fields.listItems[0];

      return formatEventTime(matchedSession.fields.start, matchedSession.fields.end);
    }
    return formatEventTime(event.start, event.end);
  };

  $: eventTime = getEventTime(event, sessionId);

  const eyebrowCta = event.parentProgram
    ? {
        text: `${event.parentProgram.title}`,
        url: sanitizeSlug(event.parentProgram.obamaorgPath)
      }
    : { text: "" };

  const image = event.image
    ? {
        src: event.image.file?.url,
        alt: event.image.description
      }
    : null;

  const eventLocation = event.specificLocation?.locationName ?? event.venue?.name; // I'm not sure we can use this for google search

  const eventAddress = event.venue?.address;

  const price = event.prices ? formatEventPrices(event.prices) : "Free";

  const getAdmissionActionBarItem = (event) => {
    const isFreeEvent = price === "Free";
    let description;
    if (event.admissionsTextOverride && event.admissionsTextOverride.length > 0) {
      description = event.admissionsTextOverride;
    } else if (isFreeEvent) {
      if (event.registrationUrl) {
        description =
          "This event is free. Please bring your confirmation email with your digital ticket(s).";
      } else {
        description = "This event is free. No registration required.";
      }
    } else {
      description = `${price}\nPlease bring your confirmation email with your digital ticket(s).`;
    }

    if (isRepeating) {
      return {
        title: "Admission",
        cta:
          event.registrationUrl && !event.hideCTAOnEventPage
            ? {
                text: event.ctaLanguage || "Get Tickets",
                type: "filled",
                url: event.registrationUrl
              }
            : null,
        description
      };
    }

    // soldOut field doesn't exist in our real sessions data currently
    const eventSoldOut = event.sessions?.soldOut ? event.sessions[0].soldOut : false;

    return {
      title: eventSoldOut ? (isFreeEvent ? "Event Full" : "Sold Out") : "Admission",
      cta:
        event.registrationUrl && !event.hideCTAOnEventPage
          ? eventSoldOut
            ? {
                text: "Join Waitlist",
                url: event.registrationUrl
              }
            : {
                text: event.ctaLanguage || "Get Tickets",
                type: "filled",
                url: event.registrationUrl
              }
          : null,
      description
    };
  };

  $: actionBar = {
    text: event.customDescription || event.description,
    showActionItemDataOnMobile: true,
    actions: [
      {
        title: isRepeating && !sessionId ? "Next Event" : "Event Details",
        description: `${eventTime.date} • ${eventTime.time}\n${eventLocation}`,
        cta: null
      },
      getAdmissionActionBarItem(event)
    ].filter(Boolean)
  };

  const calculateEventDisplayProps = (event, eventsToShow) => {
    if (event.epl && ["daily", "repeating"].includes(event.recurrence)) {
      // WHY?
      event.epl.fields.items = event.epl.fields.listItems
        .map((item) => {
          return {
            fields: {
              text: `${formatDate(item.fields.start, {
                weekday: "short",
                month: "short",
                day: "numeric"
              })} • ${formatTime(item.fields.start)} - ${formatTime(item.fields.end)}`,
              url: item.fields.registrationUrl
            }
          };
        })
        .slice(0, eventsToShow);
    } else if (event.epl) {
      event.epl.fields.listItems.slice(0, eventsToShow);
    }

    return event;
  };

  // The design currently caps the number of events shown at 6.
  const eventsToShow = 6;
  calculateEventDisplayProps(event, eventsToShow);

  let lastEventIndex = eventsToShow - 1;

  const handleClick = () => {
    lastEventIndex += eventsToShow;
    event = calculateEventDisplayProps(event, lastEventIndex);
  };

  const eventSchema = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: event.title,
    startDate: event.start,
    endDate: event.end,
    eventStatus: "https://schema.org/EventScheduled",
    location: {
      "@type": "Place",
      name: eventLocation,
      address: {
        "@type": "PostalAddress",
        streetAddress: eventAddress.split(",")[0],
        addressLocality: "Hyde Park",
        postalCode: "60637",
        addressRegion: "IL",
        addressCountry: "US"
      }
    },
    image: image ? [`http:${image.src}`] : [],
    description: event.description,
    offers: {
      "@type": "Offer",
      url: $page.url.href,
      price: isNaN(price.slice(1)) ? 0 : price.slice(1),
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      validFrom: event.start
    }
  };

  const buildEventSchema = (schema) => {
    const structuredEvents = [];
    const sessions = event.sessions ? Object.values(event.sessions) : [];
    const script = document.createElement("script");
    script.type = "application/ld+json";

    if (sessions.length) {
      sessions.forEach((session) => {
        structuredEvents.push({
          ...schema,
          startDate: session.start,
          endDate: session.end,
          offers: { ...schema.offers, validFrom: session.start }
        });
      });
    }

    script.textContent = JSON.stringify(structuredEvents.length ? structuredEvents : schema);
    document.head.appendChild(script);
  };

  onMount(async () => {
    sessionId = $page.url.searchParams.get("sessionId");

    gtag("event", "page_view", {
      page_title: event.title,
      page_location: location.href
    });

    buildEventSchema(eventSchema);
  });
</script>

<SEO
  data={{
    headline: event.title,
    description: event.summary,
    previewImageComponent: {
      fields: {
        imageAsset: event.previewImage
      }
    }
  }}
  noIndex={eventNoIndex}
  noFollow={eventNoIndex}
/>

<div class="mb-15 md:mb-30 lg:mb-25 flex flex-col">
  {#key sessionId}
    <HeroEventDetails
      class="mb-10 md:mb-15 lg:mb-25"
      theme={event.theme}
      title={event.title}
      {eyebrowCta}
      featuredImage={image}
      {actionBar}
    />
  {/key}

  {#if event.details?.content.length}
    <Section class="pb-25 md:pb-30 lg:pb-25 xl:pb-50">
      <SplitLayoutComponent
        data={{
          fields: {
            formFactor: "50/50",
            itemsLeft: [
              {
                fields: {
                  richTextContent: {
                    nodeType: "heading-4",
                    data: {
                      target: {}
                    },
                    content: [
                      {
                        value: "Details"
                      }
                    ]
                  }
                },
                sys: {
                  contentType: {
                    sys: {
                      id: "freeRichTextComponent"
                    }
                  }
                }
              }
            ],
            itemsRight: [
              {
                fields: {
                  richTextContent: event.details
                },
                sys: {
                  contentType: {
                    sys: {
                      id: "freeRichTextComponent"
                    }
                  }
                }
              }
            ]
          }
        }}
      />
    </Section>
  {/if}

  {#if event.speakers?.length}
    <Section class="pb-25 md:pb-30 lg:pb-25 xl:pb-50">
      <SplitLayoutComponent
        data={{
          fields: {
            formFactor: "50/50",
            itemsLeft: [
              {
                fields: {
                  richTextContent: {
                    nodeType: "heading-4",
                    data: {
                      target: {}
                    },
                    content: [
                      {
                        value: event?.speakerSectionTitle || "Speakers"
                      }
                    ]
                  }
                },
                sys: {
                  contentType: {
                    sys: {
                      id: "freeRichTextComponent"
                    }
                  }
                }
              }
            ],
            itemsRight: [
              {
                fields: {
                  items: event.speakers.map((speaker) => {
                    let contentArray = [
                      {
                        data: {},
                        nodeType: "text",
                        value: speaker.fields.blurb
                      },
                      {
                        nodeType: "text",
                        value: "\n\n"
                      }
                    ];
                    if (speaker.fields.url?.length > 0) {
                      contentArray.push({
                        nodeType: "embedded-entry-block",
                        data: {
                          target: {
                            fields: {
                              destination: {
                                fields: {
                                  url: speaker.fields.url
                                }
                              },
                              text: "Learn More",
                              type: "smallTextLink"
                            },
                            sys: {
                              contentType: {
                                sys: {
                                  id: "linkComponent"
                                }
                              }
                            }
                          }
                        },
                        content: [
                          {
                            value: "Learn More"
                          }
                        ]
                      });
                    }
                    return {
                      fields: {
                        headline: speaker.fields.name,
                        body: {
                          nodeType: "paragraph",
                          content: contentArray,
                          data: {}
                        },
                        image: {
                          fields: {
                            imageAsset: {
                              fields: {
                                alt: speaker.fields.altText,
                                file: {
                                  url: speaker.fields.image?.fields.file?.url
                                }
                              }
                            }
                          }
                        }
                      },
                      sys: {
                        contentType: {
                          sys: {
                            id: "contentGroupItem"
                          }
                        }
                      }
                    };
                  }),
                  formFactor: "List: Thumbnail"
                },
                sys: {
                  contentType: {
                    sys: {
                      id: "contentGroup"
                    }
                  }
                }
              }
            ]
          }
        }}
      ></SplitLayoutComponent>
    </Section>
  {/if}

  {#key event}
    {#if event.epl && ["repeating", "daily"].includes(event.recurrence)}
      <Section class="embedded-content">
        <SplitLayout variant="50/50">
          <div slot="leftComponent">
            <h2 class="heading-sm">{event.epl.fields.displayTitle || "Schedule"}</h2>
          </div>
          <div slot="rightComponent">
            <Static data={event.epl} linked={"registrationUrl" in event}></Static>
            {#if event.epl.fields.listItems.length >= eventsToShow}
              <div class="my-5">
                {#if event.registrationUrl}
                  <Button
                    variant="outline"
                    buttonClass="w-full justify-center"
                    url={event.registrationUrl}>Browse All Sessions</Button
                  >
                {:else}
                  <Button
                    variant="outline"
                    buttonClass="w-full justify-center"
                    on:click={handleClick}>Load more</Button
                  >
                {/if}
              </div>
            {/if}
          </div>
        </SplitLayout>
      </Section>
    {/if}
  {/key}

  <Section class="pb-25 md:pb-20 lg:pb-25 xl:pb-30" title="Plan Your Visit">
    <LocationInformation venue={event.venue} specificLocation={event.specificLocation} />
  </Section>

  <PageBody
    body={mainBody}
    class="page-body {trailingBody || epl.fields.listItems?.length
      ? '!mb-0 pb-25 md:pb-30 lg:pb-25 xl:pb-50'
      : 'last-page-element'}"
  />

  {#if epl.fields.listItems?.length && !["repeating", "daily"].includes(event.recurrence)}
    <EventPreviewList data={epl} />
  {/if}
</div>

{#if trailingBody}
  <PageBody body={trailingBody} class="page-body last-page-element" />
{/if}
